import { useQuery } from '@tanstack/react-query';

import { SNAPSHOT_EXPORT_STATUSES } from './constants';
import { snapshotExportsQuery } from './queries';

const STALE_EXPORT_WINDOW_SECONDS = 12 * 60 * 60;

export const SNAPSHOT_EXPORT_POLL_INTERVAL_MS = 10_000;

function getCurrentTimestamp() {
  return Math.floor(Date.now() / 1000);
}

function isFreshExport(snapshotExport, now) {
  return snapshotExport.timestamp >= now - STALE_EXPORT_WINDOW_SECONDS;
}

export function getSnapshotExportsStatus(snapshotExports = [], now = getCurrentTimestamp()) {
  const exportsList = snapshotExports ?? [];
  const exporting = exportsList.filter(
    (snapshotExport) =>
      snapshotExport.status === SNAPSHOT_EXPORT_STATUSES.EXPORTING && isFreshExport(snapshotExport, now),
  );
  const failed = exportsList.filter(
    (snapshotExport) =>
      snapshotExport.status === SNAPSHOT_EXPORT_STATUSES.ERROR ||
      (snapshotExport.status === SNAPSHOT_EXPORT_STATUSES.EXPORTING && !isFreshExport(snapshotExport, now)),
  );
  const completed = exportsList.filter(
    (snapshotExport) => snapshotExport.status === SNAPSHOT_EXPORT_STATUSES.COMPLETED,
  );

  return {
    exportsList,
    exporting,
    failed,
    completed,
    exportingCount: exporting.length,
    hasActiveExports: exporting.length > 0,
    isSnapshotExporting: (snapshotId) =>
      exporting.some((snapshotExport) => Number(snapshotExport.id) === Number(snapshotId)),
  };
}

export function getSnapshotExportsRefetchInterval(snapshotExports, now = getCurrentTimestamp()) {
  return getSnapshotExportsStatus(snapshotExports, now).hasActiveExports ? SNAPSHOT_EXPORT_POLL_INTERVAL_MS : false;
}

export function useSnapshotExportsStatus({ enablePolling = false, ...queryOptions } = {}) {
  const query = useQuery({
    ...snapshotExportsQuery(),
    ...(enablePolling
      ? {
          refetchInterval: (data) => getSnapshotExportsRefetchInterval(data?.snapshotExports),
        }
      : {}),
    ...queryOptions,
  });
  const status = getSnapshotExportsStatus(query.data?.snapshotExports);

  return {
    ...query,
    ...status,
  };
}
